import { cloneDeep, uniqBy } from 'lodash';

export const getChildRows = (row, rootRows) => {
  const childRows = rootRows.filter((r) => r.parentId === (row ? row.id : null));
  return childRows.length ? childRows : null;
};

export const findFilteredData = (data, search, parentId) => {
  const children = data.filter((cc) => cc.parentId === parentId);
  if (children.length === 0) {
    return [];
  } else {
    return children.reduce((acc, cc) => {
      const filteredChildren = findFilteredData(data, search, cc.id);
      if (
        cc.name.toLowerCase().includes(search.toLowerCase()) ||
        cc.id?.toString().toLowerCase().includes(search.toLowerCase()) ||
        filteredChildren.length > 0
      ) {
        return [...acc, cc, ...filteredChildren];
      }
      return acc;
    }, []);
  }
};

export const getCostCenterAccountsData = (accountsData, costCenterAccountsData, isCreateEditMode) => {
  const combinedCostCenterAccountsData = [];
  costCenterAccountsData?.fullyAssignedPayerAccountIDs?.forEach((paId) => {
    const payerAccount = accountsData?.find((account) => account.payerAccount.id === paId);
    if (payerAccount) {
      const laCount = payerAccount.linkedAccounts.length;
      const tmpPayerAccount = {
        ...payerAccount,
        payerAccount: {
          ...payerAccount.payerAccount,
          isSelectedInitial: !!isCreateEditMode,
          isSelectionEnabled: true,
          isAssignedByCurrentEntity: true,
        },
      };
      payerAccount.linkedAccounts.forEach((la) => {
        la.isPayerFullyAssigned = true;
      });
      combinedCostCenterAccountsData.push({
        ...tmpPayerAccount,
        totalPayerLinkedAccounts: laCount,
        totalSelectedPayerLinkedAccounts: laCount,
      });
    }
  });

  costCenterAccountsData?.payerWithLinkedAccounts?.forEach(({ payerAccountId, linkedAccountIds }) => {
    const payerAccount = accountsData?.find((account) => account.payerAccount.id === payerAccountId);
    if (payerAccount) {
      const selectedLinkedAccounts = payerAccount.linkedAccounts
        .filter((la) => linkedAccountIds.includes(la.id))
        .map((la) => ({
          ...la,
          isSelectedInitial: !!isCreateEditMode,
          isSelectionEnabled: true,
          isAssignedByCurrentEntity: true,
        }));
      const tmpPayerAccount = {
        payerAccount: {
          ...payerAccount.payerAccount,
          isSelectionEnabled: payerAccount?.payerAccount?.isFullyAssignable ?? true,
        },
        linkedAccounts: selectedLinkedAccounts,
        totalPayerLinkedAccounts: payerAccount.linkedAccounts.length,
        totalSelectedPayerLinkedAccounts: selectedLinkedAccounts.length,
      };
      combinedCostCenterAccountsData.push(tmpPayerAccount);
    }
  });

  if (isCreateEditMode) {
    accountsData?.forEach((account) => {
      const selectedPayerAccount = combinedCostCenterAccountsData.find(
        (cc) => cc.payerAccount.id === account.payerAccount.id,
      );
      if (!selectedPayerAccount) {
        const tmpPayerAccount = {
          ...account,
          payerAccount: {
            ...account.payerAccount,
            isSelectedInitial: false,
            isSelectionEnabled: account?.payerAccount?.isFullyAssignable ?? true,
            isAssignedByCurrentEntity: false,
          },
        };
        combinedCostCenterAccountsData.push({
          ...tmpPayerAccount,
          totalPayerLinkedAccounts: account.linkedAccounts?.length,
          totalSelectedPayerLinkedAccounts: 0,
        });
      } else {
        selectedPayerAccount.linkedAccounts = [
          ...(account.linkedAccounts.filter(
            (la) => !selectedPayerAccount.linkedAccounts.find((sla) => sla.id === la.id),
          ) || []),
          ...selectedPayerAccount.linkedAccounts,
        ];
      }
    });
  }
  return combinedCostCenterAccountsData;
};

function enrichCostCenters(
  costCentersData,
  costCenterDataAccess,
  modifiedSelectionById,
  currentRoleId,
  isViewDataAccessOnly,
) {
  // Create a map of role access data by cost center, and build the access map from costCenterDataAccess.
  const costCenterAccessMap = new Map();
  costCenterDataAccess.forEach((roleDataAccess) => {
    const items = roleDataAccess?.costCenters?.items || [];
    items.forEach((costCenterId) => {
      if (!costCenterAccessMap.has(costCenterId)) {
        costCenterAccessMap.set(costCenterId, []);
      }
      costCenterAccessMap.get(costCenterId).push(roleDataAccess);
    });
  });

  const copyOfCostCentersData = cloneDeep(costCentersData);
  copyOfCostCentersData?.forEach((costCenterData) => {
    const costCenterId = costCenterData.costCenter.id;
    const isExcludedByModifiedSelection = modifiedSelectionById && modifiedSelectionById[costCenterId] === false;
    const isIncludedByModifiedSelection = modifiedSelectionById && modifiedSelectionById[costCenterId] === true;

    if (isExcludedByModifiedSelection) {
      return;
    }

    if (costCenterAccessMap?.has(costCenterId) || isIncludedByModifiedSelection) {
      if (isViewDataAccessOnly && !costCenterAccessMap.has(costCenterData.costCenter.parentCcId)) {
        costCenterData.costCenter.parentCcId = null;
      }

      costCenterData.costCenter.isAllowed = true;

      if (!costCenterData.costCenter.assignedByRoles) {
        costCenterData.costCenter.assignedByRoles = [];
      }

      const isDirectlyAssigned =
        costCenterData.costCenter.isDirectlyAssigned ||
        isIncludedByModifiedSelection ||
        (costCenterAccessMap.has(costCenterId) &&
          costCenterAccessMap.get(costCenterId)?.some((role) => role.derivedFromRole.id === currentRoleId));

      costCenterData.costCenter.isDirectlyAssigned = isDirectlyAssigned;

      if (isDirectlyAssigned) {
        costCenterData.costCenter.assignedByRoles = [];
      } else if (costCenterAccessMap.has(costCenterId)) {
        costCenterData.costCenter.assignedByRoles = [
          ...costCenterData.costCenter.assignedByRoles,
          ...costCenterAccessMap.get(costCenterId).map((role) => role.derivedFromRole),
        ];
      }
    }
  });

  return uniqBy(copyOfCostCentersData, 'costCenter.id');
}

function mutateAddEffectiveIsAllowed(costCenters, allCostCenters = costCenters) {
  costCenters.forEach((costCenterWrapper) => {
    const costCenter = costCenterWrapper.costCenter;
    const children = allCostCenters.filter((cc) => cc.costCenter.parentCcId === costCenter.id);

    if (costCenter.isAllowed || costCenter.effectiveIsAllowed) {
      children.forEach((child) => {
        child.costCenter.effectiveIsAllowed = true;
      });
    }

    if (children.length > 0) {
      mutateAddEffectiveIsAllowed(children, allCostCenters);
    }
  });
}

export const getCostCentersDataAccessCombinedData = (
  costCentersData,
  costCenterDataAccess,
  currentRoleId,
  modifiedSelectionById,
  isViewDataAccessOnly,
) => {
  if (costCenterDataAccess.find((roleDataAccess) => roleDataAccess.costCenters.all === true)) {
    return costCentersData;
  }
  const enrichedCostCenters = enrichCostCenters(
    costCentersData,
    costCenterDataAccess,
    modifiedSelectionById,
    currentRoleId,
    isViewDataAccessOnly,
  );
  mutateAddEffectiveIsAllowed(enrichedCostCenters);

  return enrichedCostCenters;
};

export const mergeCostCentersDataWithDataAccess = (costCentersData, roleDataAccessCostCentersData) => {
  const subGroupIds = new Set(roleDataAccessCostCentersData.map((cc) => cc.costCenter.id));
  const remainingOriginals = costCentersData?.filter((cc) => !subGroupIds.has(cc.costCenter.id));

  return [...roleDataAccessCostCentersData, ...remainingOriginals];
};

export const getAssignableAccounts = (accountsData, unassignedAccountsData, costCenterAccounts) => {
  const assignableAccounts = [];

  accountsData.forEach((account) => {
    const unassignedAccount = unassignedAccountsData.find(
      (ua) => ua?.payerWLinked.payerAccountId === account.payerAccount.id,
    );

    const isFullyAssignedToCurrentCostCenter = costCenterAccounts?.fullyAssignedPayerAccountIDs?.includes(
      account.payerAccount.id,
    );

    const costCenterAssignment = costCenterAccounts?.payerWithLinkedAccounts?.find(
      (ca) => ca.payerAccountId === account.payerAccount.id,
    );

    if (isFullyAssignedToCurrentCostCenter) {
      assignableAccounts.push({
        payerAccount: {
          ...account.payerAccount,
          isFullyAssignable: false, // It's already fully assigned to this cost center
        },
        linkedAccounts: account.linkedAccounts,
      });
      return;
    }

    // If account is unassigned and/or has linked accounts in the cost center
    if (unassignedAccount || costCenterAssignment) {
      const relevantLinkedAccounts = account.linkedAccounts.filter((la) => {
        const isUnassigned = unassignedAccount?.payerWLinked?.linkedAccountIds?.includes(la.id);
        const isAssignedToCostCenter = costCenterAssignment?.linkedAccountIds?.includes(la.id);

        return isUnassigned || isAssignedToCostCenter;
      });
      if (relevantLinkedAccounts.length > 0) {
        assignableAccounts.push({
          payerAccount: {
            ...account.payerAccount,
            isFullyAssignable: unassignedAccount?.isFullyAssignable || false,
          },
          linkedAccounts: relevantLinkedAccounts,
        });
      }
    }
  });
  return assignableAccounts;
};

export function mergeCostCentersForDuplicateRole(costCentersDataFromDuplicate, newCostCentersData) {
  const mergedPayload = {
    all: false,
    itemsToAdd: [],
  };

  if (newCostCentersData?.all === true) {
    mergedPayload.all = true;
  } else {
    let updatedCostCentersDataFromDuplicate = cloneDeep(costCentersDataFromDuplicate);
    if (newCostCentersData?.itemsToRemove?.length) {
      newCostCentersData?.itemsToRemove?.forEach((ccId) => {
        if (updatedCostCentersDataFromDuplicate?.itemsToAdd?.includes(ccId)) {
          updatedCostCentersDataFromDuplicate.itemsToAdd = updatedCostCentersDataFromDuplicate?.itemsToAdd?.filter(
            (id) => id !== ccId,
          );
        }
      });
    }
    mergedPayload.itemsToAdd = [
      ...new Set([
        ...(updatedCostCentersDataFromDuplicate?.itemsToAdd || []),
        ...(newCostCentersData?.itemsToAdd || []),
      ]),
    ];
  }
  return mergedPayload;
}

export const getCostCenterAccountsCounter = (props, accountsData) => {
  const { row } = props;
  let counter = 0;
  if (accountsData && row?.accounts) {
    const accounts = row?.accounts;
    accounts?.fullyAssignedPayerAccountIDs?.forEach((paId) => {
      const pa = accountsData.find((account) => account.payerAccount.id === paId);
      if (pa?.linkedAccounts.length > 0) {
        counter += pa?.linkedAccounts.length;
      }
    });
    accounts?.payerWithLinkedAccounts?.forEach((pa) => {
      counter += pa.linkedAccountIds.length;
    });
  }
  return counter;
};

export const getCostCenterUnassignedAccountsCounter = (unassignedAccounts) => {
  let counter = 0;
  unassignedAccounts?.forEach((account) => {
    if (account?.payerWLinked?.linkedAccountIds?.length) {
      counter += account.payerWLinked.linkedAccountIds.length;
    }
  });
  return counter;
};
